// The work-volume's runtime SCENE FURNITURE: the 5 resizable wall/ceiling grid
// planes + the shared floor, the wireframe cage rebuilt each frame from the
// visible-surface mask, and the resize grab handles. Pure scene/geometry — it
// reads the live `volumeBounds` (owned by main / the bounds slice) and lays the
// meshes out from it, but never mutates it. The static specs (surface layout,
// cage edge definitions, corner()) live in ./work-volume; this slice is the
// instantiated, per-frame side.

import * as THREE from "three";
import {
  SURFACE_SPECS,
  type SurfaceSpec,
  type VolumeBounds,
  type WorkVolumeSurface,
  cageEdgeDefs,
} from "./work-volume";
import { uiState } from "../ui/state";

export interface WorkVolumeViewDeps {
  gridGroup: THREE.Group; // the walls/cage/handles ride the canvas
  groundMat: THREE.ShaderMaterial; // cloned per wall; the shared floor uses it directly
  ground: THREE.Mesh; // the floor plane (surface index 5)
  volumeBounds: VolumeBounds; // live, read-only here
  resizeHotHandle: number[]; // per-controller handle index under the laser (-1 = none)
  camera: THREE.Camera; // perspective eye for the occlusion pass
  renderer: THREE.WebGLRenderer; // xr presence + xr camera
  orbitTarget: () => THREE.Vector3; // 2D-ortho view target (lazy: declared late in main)
  isModelOrthographic: () => boolean;
}

export interface WorkVolumeViewApi {
  workVolumeSurfaces: WorkVolumeSurface[];
  resizeHandleMeshes: THREE.Mesh[];
  cage: THREE.LineSegments;
  rebuildWorkVolumeWalls: () => void;
  repositionResizeHandles: () => void;
  grabbedHandleIndex: (axis: 0 | 1 | 2, sign: -1 | 1) => number;
  updateResizeHandlesVisual: () => void;
  updateWorkVolumeVisibility: () => void;
}

const RESIZE_HANDLE_COLOR = 0x4aa3ff;
const RESIZE_HANDLE_HOT_COLOR = 0x9fd2ff;
const HANDLE_OUTSET = 0.12; // grid-local metres, floats the handle off the wall
// How far inside its own plane (gridGroup-local metres) the camera must sit for
// a wall to render. Hiding a wall the camera is right up against avoids it
// snapping on at ~zero distance, which flashes a full-screen quad for a frame as
// the view orbits past the plane.
const NEAR_WALL_HIDE = 0.2;

export function installWorkVolumeView(deps: WorkVolumeViewDeps): WorkVolumeViewApi {
  const { gridGroup, groundMat, ground, volumeBounds, resizeHotHandle } = deps;

  const workVolumeSurfaces: WorkVolumeSurface[] = [];

  // Size + place a wall's plane from the current bounds. Used at build time and
  // on every resize (rebuildWorkVolumeWalls). The grid shader fades the cell
  // pattern out past `uGridHalfSize` (a rounded-rect SDF in the plane's local
  // XY), so each wall needs its OWN footprint = its half-extents, otherwise the
  // grid clips at the initial size and the rest reads as bare passthrough. Each
  // wall gets a cloned material so it can hold that.
  function layoutWall(surface: WorkVolumeSurface, spec: SurfaceSpec): void {
    const layout = spec.layout(volumeBounds);
    surface.mesh.geometry.dispose();
    surface.mesh.geometry = new THREE.PlaneGeometry(layout.width, layout.height);
    surface.mesh.position.set(layout.pos[0], layout.pos[1], layout.pos[2]);
    surface.coord = layout.pos[spec.axis];
    const u = (surface.mesh.material as THREE.ShaderMaterial).uniforms;
    // Footprint = the plane itself (centered), so SDF center is 0.
    u.uGridHalfSize.value.set(layout.width / 2, layout.height / 2);
    u.uGridCenter.value.set(0, 0);
    // Phase = the plane center's gridGroup-local coords along this plane's two
    // in-plane axes, so the cell lines align to the absolute grid even when the
    // wall sits at an off-cell position. The in-plane axes depend on the wall's
    // normal: normal Z → (x,y); normal X → (z,y); normal Y → (x,z). (Sign is
    // irrelevant — the line set is symmetric.)
    const [px, py, pz] = layout.pos;
    if (spec.axis === 2) u.uGridOffset.value.set(px, py);
    else if (spec.axis === 0) u.uGridOffset.value.set(pz, py);
    else u.uGridOffset.value.set(px, pz);
  }

  function makeWorkVolumeWall(spec: SurfaceSpec): WorkVolumeSurface {
    const mesh = new THREE.Mesh(new THREE.PlaneGeometry(1, 1), groundMat.clone());
    mesh.rotation.set(spec.rot[0], spec.rot[1], spec.rot[2]);
    mesh.renderOrder = -1;
    gridGroup.add(mesh);
    const surface: WorkVolumeSurface = {
      mesh,
      axis: spec.axis,
      outwardSign: spec.outwardSign,
      coord: 0,
    };
    workVolumeSurfaces.push(surface);
    layoutWall(surface, spec);
    return surface;
  }

  for (const spec of SURFACE_SPECS) makeWorkVolumeWall(spec);

  // Re-size + reposition the 5 resizable walls/ceiling from the current bounds
  // (indices 0..4 match SURFACE_SPECS order; floor is fixed). The cage rebuilds
  // per frame, so it needs nothing here.
  function rebuildWorkVolumeWalls(): void {
    for (let i = 0; i < SURFACE_SPECS.length; i++) {
      layoutWall(workVolumeSurfaces[i], SURFACE_SPECS[i]);
    }
    // Floor (the shared groundMat, a plane at the origin): footprint = the X/Z
    // spans, centered on the footprint. The floor's local Y maps to gridGroup
    // −Z (its rotation.x = -π/2), so the center's Z is negated.
    const fCenterX = (volumeBounds.minX + volumeBounds.maxX) / 2;
    const fCenterZ = (volumeBounds.minZ + volumeBounds.maxZ) / 2;
    groundMat.uniforms.uGridHalfSize.value.set(
      (volumeBounds.maxX - volumeBounds.minX) / 2,
      (volumeBounds.maxZ - volumeBounds.minZ) / 2,
    );
    groundMat.uniforms.uGridCenter.value.set(fCenterX, -fCenterZ);
    repositionResizeHandles();
  }
  // Floor goes in as surface index 5 so updateWorkVolumeVisibility can hide it
  // when the camera dips below y=0 (matches the walls/ceiling treatment).
  workVolumeSurfaces.push({ mesh: ground, axis: 1, coord: 0, outwardSign: -1 });

  // Cube cage: 12 edges rebuilt each frame from the visible-surface mask (see
  // updateWorkVolumeVisibility).
  const cagePositions = new Float32Array(cageEdgeDefs.length * 6);
  const cageGeo = new THREE.BufferGeometry();
  cageGeo.setAttribute("position", new THREE.BufferAttribute(cagePositions, 3));
  const cage = new THREE.LineSegments(
    cageGeo,
    new THREE.LineBasicMaterial({
      color: 0xb2b2bf,
      transparent: true,
      opacity: 0.9,
      depthTest: true,
    }),
  );
  cage.renderOrder = -1;
  gridGroup.add(cage);

  // One grab handle per resizable wall/ceiling (indices match SURFACE_SPECS).
  // Visible only in the RESIZE top-mode. Parented to gridGroup so they ride the
  // canvas; repositioned whenever the volume rebuilds. Dedicated meshes (not the
  // wall planes) because the camera-occlusion pass hides the wall facing the
  // user.
  const resizeHandleGeom = new THREE.SphereGeometry(0.16, 16, 12);
  const resizeHandleMeshes: THREE.Mesh[] = [];
  for (let i = 0; i < SURFACE_SPECS.length; i++) {
    const m = new THREE.Mesh(
      resizeHandleGeom,
      new THREE.MeshBasicMaterial({
        color: RESIZE_HANDLE_COLOR,
        transparent: true,
        opacity: 0.92,
        depthTest: false,
      }),
    );
    m.renderOrder = 951;
    m.visible = false;
    gridGroup.add(m);
    resizeHandleMeshes.push(m);
  }

  function repositionResizeHandles(): void {
    for (let i = 0; i < SURFACE_SPECS.length; i++) {
      const surf = workVolumeSurfaces[i];
      const h = resizeHandleMeshes[i];
      h.position.copy(surf.mesh.position);
      const o = HANDLE_OUTSET * surf.outwardSign;
      if (surf.axis === 0) h.position.x += o;
      else if (surf.axis === 1) h.position.y += o;
      else h.position.z += o;
    }
  }
  repositionResizeHandles();

  // Handle index (0..4) for a given (axis, outwardSign) — each resizable wall
  // has a unique pair. Used to lock the laser to the grabbed handle.
  function grabbedHandleIndex(axis: 0 | 1 | 2, sign: -1 | 1): number {
    for (let i = 0; i < SURFACE_SPECS.length; i++) {
      const s = workVolumeSurfaces[i];
      if (s.axis === axis && s.outwardSign === sign) return i;
    }
    return -1;
  }

  // Show handles only in resize mode; tint the one(s) under a laser. Runs
  // every frame but resize mode is rare — dirty-check so the common case is a
  // three-compare early return instead of per-handle material writes.
  let _handlesShown = false;
  let _hot0 = -1;
  let _hot1 = -1;
  function updateResizeHandlesVisual(): void {
    const resizing = uiState.topMode === "resize";
    const h0 = resizing ? resizeHotHandle[0] : -1;
    const h1 = resizing ? resizeHotHandle[1] : -1;
    if (resizing === _handlesShown && h0 === _hot0 && h1 === _hot1) return;
    _handlesShown = resizing;
    _hot0 = h0;
    _hot1 = h1;
    for (let i = 0; i < resizeHandleMeshes.length; i++) {
      const m = resizeHandleMeshes[i];
      m.visible = resizing;
      const hot = h0 === i || h1 === i;
      (m.material as THREE.MeshBasicMaterial).color.setHex(
        hot ? RESIZE_HANDLE_HOT_COLOR : RESIZE_HANDLE_COLOR,
      );
    }
  }

  const _camLocalTmp = new THREE.Vector3();
  // Cage rebuild inputs from the previous frame. The wall-side classification
  // flips rarely (the camera has to cross a wall plane), and the bounds only
  // change on resize — so the common frame is: recompute the 6-bit mask, see
  // nothing changed, and skip the cage rewrite + GPU re-upload entirely.
  let _cageMask = -1;
  let _cageMinX = NaN;
  let _cageMaxX = NaN;
  let _cageMinZ = NaN;
  let _cageMaxZ = NaN;
  let _cageHeight = NaN;
  function updateWorkVolumeVisibility(): void {
    // Project the camera into grid-local space, then hide any surface the camera
    // is on the outward side of (it would block the view into the cube) or
    // within a short margin of (the near-plane flash). Cage edges stay
    // always-visible — they're the scaffold.
    const { camera, renderer } = deps;
    if (!renderer.xr.isPresenting && uiState.mode === "modeling" && deps.isModelOrthographic()) {
      // 2D ortho: a parallel projection's eye is effectively at infinity along
      // the view axis. Use a far-back point rather than the perspective eye
      // (which dollies INTO the box as you zoom) so the wall facing you is
      // ALWAYS hidden. Otherwise, mid-zoom it flips visible and — rendered in
      // the ortho depth slab where it tests nearer than the voxels — covers the
      // model ("faces vanish").
      camera.getWorldDirection(_camLocalTmp); // forward, toward the scene
      _camLocalTmp.multiplyScalar(-1e4).add(deps.orbitTarget());
    } else {
      const cam = renderer.xr.isPresenting ? renderer.xr.getCamera() : camera;
      cam.getWorldPosition(_camLocalTmp);
    }
    // Ancestors + self only — the children get their world matrices from the
    // renderer.render() that follows this call; forcing the whole voxel
    // subtree here (updateMatrixWorld(true)) was redundant work every frame.
    gridGroup.updateWorldMatrix(true, false);
    gridGroup.worldToLocal(_camLocalTmp);
    let mask = 0;
    for (let i = 0; i < workVolumeSurfaces.length; i++) {
      const s = workVolumeSurfaces[i];
      const c =
        s.axis === 0 ? _camLocalTmp.x : s.axis === 1 ? _camLocalTmp.y : _camLocalTmp.z;
      // Signed distance from the wall toward the volume interior; positive = the
      // camera is inside this wall. Show the wall only when comfortably inside.
      const inwardDist = (s.coord - c) * s.outwardSign;
      const vis = inwardDist > NEAR_WALL_HIDE;
      s.mesh.visible = vis;
      if (vis) mask |= 1 << i;
    }

    const b = volumeBounds;
    if (
      mask === _cageMask &&
      b.minX === _cageMinX &&
      b.maxX === _cageMaxX &&
      b.minZ === _cageMinZ &&
      b.maxZ === _cageMaxZ &&
      b.height === _cageHeight
    ) {
      return; // same walls, same bounds — the cage buffer is already right
    }
    _cageMask = mask;
    _cageMinX = b.minX;
    _cageMaxX = b.maxX;
    _cageMinZ = b.minZ;
    _cageMaxZ = b.maxZ;
    _cageHeight = b.height;

    // Rebuild the cage from edges whose adjacent surfaces are all visible.
    // Corners are resolved inline (see corner() in ./work-volume for the sign
    // encoding) — the helper returns a fresh array, which per-frame code
    // can't afford.
    let writeIdx = 0;
    for (const def of cageEdgeDefs) {
      let visible = true;
      for (const surfIdx of def.surfaces) {
        if (!(mask & (1 << surfIdx))) {
          visible = false;
          break;
        }
      }
      if (!visible) continue;
      const ca = def.a;
      const cb = def.b;
      cagePositions[writeIdx++] = ca[0] < 0 ? b.minX : b.maxX;
      cagePositions[writeIdx++] = ca[1] > 0 ? b.height : 0;
      cagePositions[writeIdx++] = ca[2] < 0 ? b.minZ : b.maxZ;
      cagePositions[writeIdx++] = cb[0] < 0 ? b.minX : b.maxX;
      cagePositions[writeIdx++] = cb[1] > 0 ? b.height : 0;
      cagePositions[writeIdx++] = cb[2] < 0 ? b.minZ : b.maxZ;
    }
    cageGeo.setDrawRange(0, writeIdx / 3);
    (cageGeo.getAttribute("position") as THREE.BufferAttribute).needsUpdate = true;
  }

  return {
    workVolumeSurfaces,
    resizeHandleMeshes,
    cage,
    rebuildWorkVolumeWalls,
    repositionResizeHandles,
    grabbedHandleIndex,
    updateResizeHandlesVisual,
    updateWorkVolumeVisibility,
  };
}
